/**
 * Complete domain catalog for the Unwritten system
 *
 * 20 domains total:
 * - 6 core narrative domains
 * - 1 narrative parent domain + 7 sub-domains (hierarchical)
 * - 4 entity domains
 * - 5 world/game domains
 * - 4 system domains
 * - 2 mechanics domains
 *
 * @module catalog
 */
/**
 * Complete domain catalog
 */
export const DOMAIN_CATALOG = [
    // ============================================================================
    // CORE NARRATIVE DOMAINS (6)
    // ============================================================================
    {
        id: 'memory',
        name: 'Memory',
        description: 'Three-facet memory system with semantic search and resonance tracking',
        category: 'narrative',
        icon: 'brain',
        color: '#3B82F6', // Blue-500
        plural: 'Memories',
        tables: ['memory_facet', 'memory_pattern', 'memory_resonance'],
        relationships: [
            {
                to: 'npc',
                via: 'metadata.npcIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Memories can involve multiple NPCs'
            },
            {
                to: 'beat',
                via: 'memoryId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Beats can reference memories'
            },
            {
                to: 'card',
                via: 'metadata.memoryIds',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Cards can reference memories'
            }
        ],
        modifiers: [],
        modifiedBy: [
            {
                from: 'narrative/resonance',
                field: 'resonance_metadata',
                description: 'Resonance system calculates memory callbacks'
            }
        ],
        database: 'surrealdb-runtime',
        ragConfig: {
            fields: ['content_embedding', 'semantic_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        service: {
            serviceName: 'MemoryService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/memory',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'template',
        name: 'Template',
        description: 'Card templates with generation rules and validation schemas',
        category: 'narrative',
        icon: 'file-text',
        color: '#8B5CF6', // Violet-500
        plural: 'Templates',
        tables: ['template'],
        relationships: [
            {
                to: 'card',
                via: 'templateId',
                type: 'generates',
                cardinality: 'one-to-many',
                description: 'Templates generate multiple cards'
            }
        ],
        modifiers: [],
        modifiedBy: [
            {
                from: 'modifier',
                field: 'template_modifiers',
                description: 'Game/world modifiers affect template generation'
            }
        ],
        database: 'surrealdb-design',
        aiIntegration: {
            agents: ['template-generator', 'balance-analyzer', 'template-validator'],
            requiresAI: true
        },
        service: {
            serviceName: 'TemplateService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/template',
            operations: ['create', 'read', 'update', 'delete', 'search'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'card',
        name: 'Card',
        description: 'Narrative cards with choices, outcomes, and conditional logic',
        category: 'narrative',
        icon: 'square',
        color: '#6366F1', // Indigo-500
        plural: 'Cards',
        tables: ['card'],
        relationships: [
            {
                to: 'template',
                via: 'templateId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Cards instantiate from templates'
            },
            {
                to: 'memory',
                via: 'metadata.memoryIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Cards can reference memories'
            },
            {
                to: 'goal',
                via: 'metadata.goalIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Cards can relate to goals'
            },
            {
                to: 'beat',
                via: 'cardId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Beats use cards'
            }
        ],
        modifiers: [],
        modifiedBy: [
            {
                from: 'narrative/perception',
                field: 'perception_filters',
                description: 'Personality filters affect card presentation'
            },
            {
                from: 'modifier',
                field: 'card_modifiers',
                description: 'Game/world modifiers affect card properties'
            }
        ],
        database: 'surrealdb-runtime',
        replicatedIn: ['firestore'],
        sourceOfTruth: 'surrealdb-runtime',
        service: {
            serviceName: 'CardService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/card',
            operations: ['create', 'read', 'update', 'delete', 'search'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'beat',
        name: 'Beat',
        description: 'Journey beats representing story moments and player choices',
        category: 'narrative',
        icon: 'zap',
        color: '#A855F7', // Purple-500
        plural: 'Beats',
        tables: ['journey_beat'],
        relationships: [
            {
                to: 'card',
                via: 'cardId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Beats reference cards'
            },
            {
                to: 'memory',
                via: 'memoryId',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Beats can create or reference memories'
            },
            {
                to: 'goal',
                via: 'goalId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Beats can advance goals'
            },
            {
                to: 'npc',
                via: 'metadata.npcIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Beats can involve NPCs'
            },
            {
                to: 'season',
                via: 'seasonId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Beats belong to seasons'
            }
        ],
        modifiers: [],
        modifiedBy: [
            {
                from: 'narrative/hooks',
                field: 'hook_metadata',
                description: 'Hooks system tracks mysteries and reveals'
            }
        ],
        database: 'surrealdb-runtime',
        ragConfig: {
            fields: ['narrative_embedding'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        service: {
            serviceName: 'BeatService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/beat',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'goal',
        name: 'Goal',
        description: 'Player and NPC goals with progress tracking and completion logic',
        category: 'narrative',
        icon: 'target',
        color: '#EC4899', // Pink-500
        plural: 'Goals',
        tables: ['goal'],
        relationships: [
            {
                to: 'npc',
                via: 'npcId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Goals can belong to NPCs'
            },
            {
                to: 'player',
                via: 'playerId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Goals can belong to players'
            },
            {
                to: 'beat',
                via: 'goalId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Beats can advance goals'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        aiIntegration: {
            agents: ['goal-generator'],
            requiresAI: true
        },
        service: {
            serviceName: 'GoalService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/goal',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'canon',
        name: 'Canon',
        description: 'World lore and canonical facts with consistency tracking',
        category: 'narrative',
        icon: 'book',
        color: '#F59E0B', // Amber-500
        plural: 'Canon',
        tables: ['canon_entry'],
        relationships: [
            {
                to: 'world',
                via: 'worldId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Canon entries belong to worlds'
            },
            {
                to: 'npc',
                via: 'metadata.npcIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Canon can reference NPCs'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        ragConfig: {
            fields: ['content_embedding', 'lore_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['lore-generator', 'world-orchestrator', 'canon-auditor'],
            requiresAI: true
        },
        service: {
            serviceName: 'CanonService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/canon',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: true
        },
        aiOperations: [
            {
                operationId: 'world-creation-lore',
                description: 'Generate world lore entries during world creation',
                agents: ['lore-generator', 'world-orchestrator'],
                inputSchema: {
                    required: ['genre', 'tone', 'style'],
                    optional: ['themes', 'inspirations', 'constraints'],
                    example: {
                        genre: 'cyberpunk',
                        tone: 'noir',
                        style: 'gritty',
                        themes: ['corporate-dystopia', 'transhumanism']
                    }
                },
                outputSchema: {
                    targetDomains: ['canon'],
                    example: {
                        canonType: 'world-lore',
                        title: 'The Neon Underbelly',
                        content: 'In the sprawling megacity...',
                        tags: ['worldbuilding', 'cyberpunk', 'setting']
                    }
                },
                handledByService: 'CanonService',
                flow: 'User Input → WorldOrchestrator → LoreGenerator → CanonService → canon_entry (SurrealDB Design)'
            },
            {
                operationId: 'canon-consistency-check',
                description: 'Validate new content against existing canon',
                agents: ['canon-auditor'],
                inputSchema: {
                    required: ['newContent', 'worldId'],
                    optional: ['strictMode'],
                    example: {
                        newContent: { type: 'faction', name: 'NeoTech Corp' },
                        worldId: 'world_123',
                        strictMode: true
                    }
                },
                outputSchema: {
                    targetDomains: ['canon'],
                    example: {
                        isConsistent: true,
                        conflicts: [],
                        suggestions: []
                    }
                },
                handledByService: 'CanonService',
                flow: 'New Content → CanonAuditor → CanonService → Consistency Report'
            }
        ],
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    // ============================================================================
    // NARRATIVE PARENT + SUB-DOMAINS (1 + 7)
    // ============================================================================
    {
        id: 'narrative',
        name: 'Narrative',
        description: 'Parent domain for advanced narrative systems and techniques',
        category: 'narrative',
        icon: 'layers',
        color: '#10B981', // Emerald-500
        plural: 'Narrative Systems',
        tables: [],
        relationships: [],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/irony',
        name: 'Dramatic Irony',
        description: 'Dramatic irony system with POV-specific card variations',
        category: 'narrative',
        icon: 'eye-off',
        color: '#14B8A6', // Teal-500
        plural: 'Irony Moments',
        parent: 'narrative',
        tables: ['narrative_irony'],
        relationships: [
            {
                to: 'card',
                via: 'cardId',
                type: 'modifies',
                cardinality: 'one-to-many',
                description: 'Irony creates alternate card presentations'
            },
            {
                to: 'npc',
                via: 'knownByNpcIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Tracks which NPCs know the truth'
            }
        ],
        modifiers: [
            {
                from: 'card',
                field: 'irony_variations',
                description: 'Creates POV-specific card variations'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        service: {
            serviceName: 'IronyService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/irony',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/hooks',
        name: 'Narrative Hooks',
        description: 'Mystery and reveal system with unresolved question tracking',
        category: 'narrative',
        icon: 'help-circle',
        color: '#06B6D4', // Cyan-500
        plural: 'Hooks',
        parent: 'narrative',
        tables: ['narrative_hook'],
        relationships: [
            {
                to: 'beat',
                via: 'hookId',
                type: 'modifies',
                cardinality: 'many-to-many',
                description: 'Hooks are planted and resolved in beats'
            },
            {
                to: 'card',
                via: 'metadata.hookIds',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Cards can reference hooks'
            }
        ],
        modifiers: [
            {
                from: 'beat',
                field: 'hook_status',
                description: 'Tracks hook status (planted/resolved)'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        ragConfig: {
            fields: ['mystery_embedding'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['hook-analyzer'],
            requiresAI: true
        },
        service: {
            serviceName: 'HooksService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/hooks',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/perception',
        name: 'Perception',
        description: 'Personality-filtered choice presentation system',
        category: 'narrative',
        icon: 'eye',
        color: '#0EA5E9', // Sky-500
        plural: 'Perceptions',
        parent: 'narrative',
        tables: ['narrative_perception'],
        relationships: [
            {
                to: 'card',
                via: 'cardId',
                type: 'modifies',
                cardinality: 'many-to-many',
                description: 'Filters card choices based on personality'
            },
            {
                to: 'personality',
                via: 'personalityFilter',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Uses personality traits for filtering'
            }
        ],
        modifiers: [
            {
                from: 'card',
                field: 'perception_filters',
                description: 'Filters choices based on personality'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        aiIntegration: {
            agents: ['perception-filter'],
            requiresAI: true
        },
        service: {
            serviceName: 'PerceptionService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/perception',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/emotion',
        name: 'Emotional Journey',
        description: 'Emotional state tracking and journey mapping',
        category: 'narrative',
        icon: 'heart',
        color: '#EF4444', // Red-500
        plural: 'Emotions',
        parent: 'narrative',
        tables: ['narrative_emotion'],
        relationships: [
            {
                to: 'beat',
                via: 'beatId',
                type: 'modifies',
                cardinality: 'one-to-many',
                description: 'Tracks emotional impact of beats'
            },
            {
                to: 'player',
                via: 'playerId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Emotional state belongs to player'
            }
        ],
        modifiers: [
            {
                from: 'beat',
                field: 'emotional_impact',
                description: 'Tracks emotional state changes'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        aiIntegration: {
            agents: ['emotion-analyzer'],
            requiresAI: true
        },
        service: {
            serviceName: 'EmotionService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/emotion',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/observation',
        name: 'Observation',
        description: 'NPC observation and reaction system for player actions',
        category: 'narrative',
        icon: 'users',
        color: '#F59E0B', // Amber-500
        plural: 'Observations',
        parent: 'narrative',
        tables: ['narrative_observation'],
        relationships: [
            {
                to: 'npc',
                via: 'observerNpcId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Which NPC made the observation'
            },
            {
                to: 'beat',
                via: 'beatId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'What beat was observed'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        aiIntegration: {
            agents: ['observation-analyzer'],
            requiresAI: true
        },
        service: {
            serviceName: 'ObservationService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/observation',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/resonance',
        name: 'Resonance',
        description: 'Memory callback and thematic echo system',
        category: 'narrative',
        icon: 'radio',
        color: '#8B5CF6', // Violet-500
        plural: 'Resonances',
        parent: 'narrative',
        tables: ['narrative_resonance'],
        relationships: [
            {
                to: 'memory',
                via: 'memoryId',
                type: 'modifies',
                cardinality: 'many-to-many',
                description: 'Calculates memory resonance scores'
            },
            {
                to: 'beat',
                via: 'beatId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Resonance triggered by beats'
            }
        ],
        modifiers: [
            {
                from: 'memory',
                field: 'resonance_score',
                description: 'Calculates resonance with current context'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        ragConfig: {
            fields: ['theme_embedding'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['resonance-calculator'],
            requiresAI: true
        },
        service: {
            serviceName: 'ResonanceService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/resonance',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    {
        id: 'narrative/novel',
        name: 'Novel',
        description: 'Chapter summaries and character arc tracking',
        category: 'narrative',
        icon: 'book-open',
        color: '#A855F7', // Purple-500
        plural: 'Novel Elements',
        parent: 'narrative',
        tables: ['narrative_chapter', 'narrative_arc'],
        relationships: [
            {
                to: 'season',
                via: 'seasonId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Novel metadata for season'
            },
            {
                to: 'npc',
                via: 'npcId',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Character arcs for NPCs'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        ragConfig: {
            fields: ['chapter_summary_embedding', 'arc_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['chapter-summarizer', 'arc-tracker'],
            requiresAI: true
        },
        service: {
            serviceName: 'NovelService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/narrative/novel',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0',
            experimental: true
        }
    },
    // ============================================================================
    // ENTITY DOMAINS (4)
    // ============================================================================
    {
        id: 'character',
        name: 'Character',
        description: 'Player character definition and progression',
        category: 'entity',
        icon: 'user',
        color: '#10B981', // Emerald-500
        plural: 'Characters',
        tables: ['character'],
        relationships: [
            {
                to: 'player',
                via: 'playerId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Characters belong to players'
            },
            {
                to: 'personality',
                via: 'personalityId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Character has personality traits'
            }
        ],
        modifiers: [],
        modifiedBy: [
            {
                from: 'capacity',
                field: 'capacity_modifiers',
                description: 'Capacity affects character abilities'
            }
        ],
        database: 'surrealdb-runtime',
        service: {
            serviceName: 'CharacterService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/character',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'npc',
        name: 'NPC',
        description: 'Non-player characters with relationships and states',
        category: 'entity',
        icon: 'users',
        color: '#059669', // Emerald-600
        plural: 'NPCs',
        tables: ['npc', 'npc_relationship'],
        relationships: [
            {
                to: 'memory',
                via: 'metadata.npcIds',
                type: 'referenced_by',
                cardinality: 'many-to-many',
                description: 'Memories involve NPCs'
            },
            {
                to: 'goal',
                via: 'npcId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'NPCs have goals'
            },
            {
                to: 'beat',
                via: 'metadata.npcIds',
                type: 'referenced_by',
                cardinality: 'many-to-many',
                description: 'NPCs participate in beats'
            },
            {
                to: 'personality',
                via: 'personalityId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'NPCs have personality traits'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        ragConfig: {
            fields: ['profile_embedding', 'relationship_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['npc-generator', 'relationship-analyzer'],
            requiresAI: true
        },
        service: {
            serviceName: 'NPCService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/npc',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'player',
        name: 'Player',
        description: 'Player account with preferences and progression',
        category: 'entity',
        icon: 'user-circle',
        color: '#14B8A6', // Teal-500
        plural: 'Players',
        tables: ['player'],
        relationships: [
            {
                to: 'character',
                via: 'playerId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Players have characters'
            },
            {
                to: 'season',
                via: 'playerId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Players have seasons'
            },
            {
                to: 'goal',
                via: 'playerId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Players have goals'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'firestore',
        replicatedIn: ['surrealdb-runtime'],
        sourceOfTruth: 'firestore',
        service: {
            serviceName: 'PlayerService',
            package: 'backend-api',
            importPath: '@unwritten/backend-api/player',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'season',
        name: 'Season',
        description: 'Game season/playthrough with beats and progression',
        category: 'entity',
        icon: 'calendar',
        color: '#06B6D4', // Cyan-500
        plural: 'Seasons',
        tables: ['season'],
        relationships: [
            {
                to: 'player',
                via: 'playerId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Seasons belong to players'
            },
            {
                to: 'game',
                via: 'gameId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Seasons use game configuration'
            },
            {
                to: 'beat',
                via: 'seasonId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Seasons contain beats'
            }
        ],
        modifiers: [],
        modifiedBy: [
            {
                from: 'burnout',
                field: 'burnout_modifiers',
                description: 'Burnout affects season pacing'
            }
        ],
        database: 'surrealdb-runtime',
        service: {
            serviceName: 'SeasonService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/season',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    // ============================================================================
    // WORLD/GAME DOMAINS (5)
    // ============================================================================
    {
        id: 'universe',
        name: 'Universe',
        description: 'Universe configuration defining what worlds are possible',
        category: 'world',
        icon: 'infinity',
        color: '#F59E0B', // Amber-500
        plural: 'Universes',
        tables: ['universe_config'],
        relationships: [
            {
                to: 'world',
                via: 'universeId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Universe defines possible worlds'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        service: {
            serviceName: 'UniverseService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/universe',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    {
        id: 'world',
        name: 'World',
        description: 'Player-selected world configuration and settings',
        category: 'world',
        icon: 'globe',
        color: '#FBBF24', // Amber-400
        plural: 'Worlds',
        tables: ['world'],
        relationships: [
            {
                to: 'universe',
                via: 'universeId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'World derives from universe'
            },
            {
                to: 'game',
                via: 'worldId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'World generates game configurations'
            },
            {
                to: 'canon',
                via: 'worldId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'World contains canon entries'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        ragConfig: {
            fields: ['description_embedding', 'theme_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['world-orchestrator', 'lore-generator', 'setting-generator'],
            requiresAI: true
        },
        service: {
            serviceName: 'WorldService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/world',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    {
        id: 'game',
        name: 'Game',
        description: 'Game instance with active modifiers and rules',
        category: 'world',
        icon: 'gamepad-2',
        color: '#FB923C', // Orange-400
        plural: 'Games',
        tables: ['game'],
        relationships: [
            {
                to: 'world',
                via: 'worldId',
                type: 'references',
                cardinality: 'many-to-one',
                description: 'Game derives from world'
            },
            {
                to: 'season',
                via: 'gameId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Game used by seasons'
            },
            {
                to: 'modifier',
                via: 'activeModifierIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Game applies modifiers'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        service: {
            serviceName: 'GameService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/game',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    {
        id: 'setting',
        name: 'Setting',
        description: 'Narrative setting and location information',
        category: 'world',
        icon: 'map-pin',
        color: '#F59E0B', // Amber-500
        plural: 'Settings',
        tables: ['setting'],
        relationships: [
            {
                to: 'world',
                via: 'worldId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Settings belong to worlds'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        ragConfig: {
            fields: ['description_embedding', 'location_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['setting-generator'],
            requiresAI: true
        },
        service: {
            serviceName: 'SettingService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/setting',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    {
        id: 'society',
        name: 'Society',
        description: 'Social structures and cultural systems',
        category: 'world',
        icon: 'building-2',
        color: '#FBBF24', // Amber-400
        plural: 'Societies',
        tables: ['society'],
        relationships: [
            {
                to: 'world',
                via: 'worldId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Societies belong to worlds'
            },
            {
                to: 'npc',
                via: 'societyId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'NPCs belong to societies'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        ragConfig: {
            fields: ['culture_embedding', 'social_structure_vector'],
            dimensions: 768,
            distanceMetric: 'cosine',
            embeddingModel: 'text-embedding-3-small',
            indexType: 'mtree'
        },
        aiIntegration: {
            agents: ['culture-generator'],
            requiresAI: true
        },
        service: {
            serviceName: 'SocietyService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/society',
            operations: ['create', 'read', 'update', 'delete', 'search', 'embed'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    // ============================================================================
    // SYSTEM DOMAINS (4)
    // ============================================================================
    {
        id: 'modifier',
        name: 'Modifier',
        description: 'Game and world modifiers affecting domain properties',
        category: 'systems',
        icon: 'sliders-horizontal',
        color: '#EF4444', // Red-500
        plural: 'Modifiers',
        tables: ['modifier'],
        relationships: [
            {
                to: 'game',
                via: 'activeModifierIds',
                type: 'referenced_by',
                cardinality: 'many-to-many',
                description: 'Modifiers applied by games'
            },
            {
                to: 'template',
                via: 'modifierIds',
                type: 'modifies',
                cardinality: 'many-to-many',
                description: 'Modifiers affect templates'
            },
            {
                to: 'card',
                via: 'modifierIds',
                type: 'modifies',
                cardinality: 'many-to-many',
                description: 'Modifiers affect cards'
            }
        ],
        modifiers: [
            {
                from: 'template',
                field: 'template_modifiers',
                description: 'Modifies template properties'
            },
            {
                from: 'card',
                field: 'card_modifiers',
                description: 'Modifies card properties'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-design',
        service: {
            serviceName: 'ModifierService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/modifier',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    {
        id: 'bias',
        name: 'Bias',
        description: 'System bias configuration and tracking',
        category: 'systems',
        icon: 'trending-up',
        color: '#F43F5E', // Rose-500
        plural: 'Biases',
        tables: ['bias'],
        relationships: [
            {
                to: 'game',
                via: 'biasIds',
                type: 'references',
                cardinality: 'many-to-many',
                description: 'Games can configure biases'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-design',
        service: {
            serviceName: 'BiasService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/bias',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    {
        id: 'capacity',
        name: 'Capacity',
        description: 'Player and NPC capacity tracking for abilities',
        category: 'systems',
        icon: 'battery',
        color: '#EC4899', // Pink-500
        plural: 'Capacities',
        tables: ['capacity'],
        relationships: [
            {
                to: 'character',
                via: 'characterId',
                type: 'modifies',
                cardinality: 'many-to-one',
                description: 'Capacity affects character abilities'
            },
            {
                to: 'npc',
                via: 'npcId',
                type: 'modifies',
                cardinality: 'many-to-one',
                description: 'Capacity affects NPC abilities'
            }
        ],
        modifiers: [
            {
                from: 'character',
                field: 'capacity_value',
                description: 'Modifies available capacity'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        service: {
            serviceName: 'CapacityService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/capacity',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'burnout',
        name: 'Burnout',
        description: 'Burnout tracking and pacing system',
        category: 'systems',
        icon: 'flame',
        color: '#F97316', // Orange-500
        plural: 'Burnout',
        tables: ['burnout'],
        relationships: [
            {
                to: 'season',
                via: 'seasonId',
                type: 'modifies',
                cardinality: 'many-to-one',
                description: 'Burnout affects season pacing'
            }
        ],
        modifiers: [
            {
                from: 'season',
                field: 'burnout_modifiers',
                description: 'Adjusts season pacing based on burnout'
            }
        ],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        service: {
            serviceName: 'BurnoutService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/burnout',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    // ============================================================================
    // MECHANICS DOMAINS (2)
    // ============================================================================
    {
        id: 'personality',
        name: 'Personality',
        description: 'Personality traits and behavioral patterns',
        category: 'mechanics',
        icon: 'heart',
        color: '#14B8A6', // Teal-500
        plural: 'Personalities',
        tables: ['personality'],
        relationships: [
            {
                to: 'character',
                via: 'personalityId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Characters have personalities'
            },
            {
                to: 'npc',
                via: 'personalityId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'NPCs have personalities'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        aiIntegration: {
            agents: ['personality-generator'],
            requiresAI: true
        },
        service: {
            serviceName: 'PersonalityService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/personality',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'resources',
        name: 'Resources',
        description: 'Player and NPC resource management',
        category: 'mechanics',
        icon: 'coins',
        color: '#0EA5E9', // Sky-500
        plural: 'Resources',
        tables: ['resources'],
        relationships: [
            {
                to: 'character',
                via: 'characterId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Resources belong to characters'
            },
            {
                to: 'npc',
                via: 'npcId',
                type: 'stored_in',
                cardinality: 'many-to-one',
                description: 'Resources belong to NPCs'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'surrealdb-runtime',
        service: {
            serviceName: 'ResourcesService',
            package: 'game-primitives',
            importPath: '@unwritten/game-primitives/resources',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: false
        },
        metadata: {
            status: 'draft',
            version: '0.1.0'
        }
    },
    // ============================================================================
    // AI SYSTEMS DOMAINS (3)
    // ============================================================================
    {
        id: 'agent',
        name: 'AI Agent',
        description: 'AI agents with execution tracking, health monitoring, and dependencies',
        category: 'systems',
        icon: 'cpu',
        color: '#6366F1', // Indigo-500
        plural: 'Agents',
        tables: ['ai_agents'], // Firestore collection
        relationships: [
            {
                to: 'flow',
                via: 'flowIds',
                type: 'executes',
                cardinality: 'one-to-many',
                description: 'Agents execute flows'
            },
            {
                to: 'memory',
                via: 'agentId',
                type: 'generates',
                cardinality: 'one-to-many',
                description: 'Memory agents generate memories'
            },
            {
                to: 'template',
                via: 'agentId',
                type: 'analyzes',
                cardinality: 'one-to-many',
                description: 'Template agents analyze and validate templates'
            },
            {
                to: 'goal',
                via: 'agentId',
                type: 'generates',
                cardinality: 'one-to-many',
                description: 'Goal agents generate character goals'
            }
        ],
        modifiers: [
            {
                from: 'template',
                field: 'template_evolution',
                description: 'Template evolution agent modifies template tiers'
            },
            {
                from: 'memory',
                field: 'resonance_score',
                description: 'Memory resonance agent calculates resonance'
            }
        ],
        modifiedBy: [],
        database: 'firestore',
        aiIntegration: {
            requiresAI: true
        },
        service: {
            serviceName: 'AgentService',
            package: 'backend-api',
            importPath: '@unwritten/backend-api/agents',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'flow',
        name: 'AI Flow',
        description: 'AI flow definitions with model configs and dependencies',
        category: 'systems',
        icon: 'git-branch',
        color: '#8B5CF6', // Violet-500
        plural: 'Flows',
        tables: ['ai_flows'], // Firestore collection
        relationships: [
            {
                to: 'agent',
                via: 'dependencies',
                type: 'referenced_by',
                cardinality: 'many-to-many',
                description: 'Flows are executed by agents'
            },
            {
                to: 'model',
                via: 'modelId',
                type: 'uses',
                cardinality: 'many-to-one',
                description: 'Flows use AI models'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'firestore',
        aiIntegration: {
            requiresAI: true
        },
        service: {
            serviceName: 'FlowService',
            package: 'backend-api',
            importPath: '@unwritten/backend-api/flows',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    },
    {
        id: 'model',
        name: 'AI Model',
        description: 'AI model registry with capabilities and performance metrics',
        category: 'systems',
        icon: 'zap',
        color: '#F59E0B', // Amber-500
        plural: 'Models',
        tables: ['ai_models'], // Firestore collection
        relationships: [
            {
                to: 'flow',
                via: 'modelId',
                type: 'referenced_by',
                cardinality: 'one-to-many',
                description: 'Models are used by flows'
            }
        ],
        modifiers: [],
        modifiedBy: [],
        database: 'firestore',
        service: {
            serviceName: 'ModelService',
            package: 'backend-api',
            importPath: '@unwritten/backend-api/models',
            operations: ['create', 'read', 'update', 'delete'],
            implemented: true
        },
        metadata: {
            status: 'implemented',
            version: '1.0.0'
        }
    }
];
//# sourceMappingURL=catalog.js.map